import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '../../providers/api/api.service';
import { Device, IDeviceData } from '../../providers/device/device';
import { LmapService } from '../../services/lmap-service/lmap';
import { TestAssembleService } from '../../services/lmap-service/test-assemble';
import { TestServerConfigService } from '../../services/lmap-service/testServerConfig';
import { Subject, Observable } from 'rxjs';
import { RunTypeValue, ScheduledIntervals } from '../../services/test-list/types';


type outputParams = {
  //eslint-disable-next-line
  [key: string]: string | any;
};

@Injectable({
  providedIn: 'root'
})
export class AppsMultiStepSetupService {
  state: ActivatedRoute;
  lmapService: LmapService;
  output: outputParams;
  testAssembleService: TestAssembleService;
  serverConfigService: TestServerConfigService;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tabDataChangeEvents = new Subject<any>();
  testTaskName = 'multistep';
  deviceModel = 'vTA';
  selectedOriginator: Device;
  selectedDestination: Device;
  selectedTestType = {
    name: 'Web Transactions',
    description: 'Monitor web performance metrics of a defined application',
    id: 'multistep'
  };
  monitorId = '';
  tenantId = 'paas.local';
  siteList = [];
  selectedDevices = [];
  selectedSites = [];
  TEST_LIMIT_COUNT = 2000;
  runType = RunTypeValue.ON_GOING;
  startTestURL = '/uicomm/StartTest';
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(
    state: ActivatedRoute,
    lmapService: LmapService,
    testAssmebleService: TestAssembleService,
    serverConfigService: TestServerConfigService,
    private readonly apiService: ApiService,
  ) {
    this.output = {};
    this.state = state;
    this.lmapService = lmapService;
    this.testAssembleService = testAssmebleService;
    this.serverConfigService = serverConfigService;
  }

  //eslint-disable-next-line
  createLMAPObject(): any {
    this.runType = this.output.runOnDemand;
    const testPointA = this.output['selectedOriginator']?.deviceData;
    const serverConfig = this.serverConfigService.getCollectorConfig();
    const collectorURL = this.lmapService.buildCollectorPostUrl(serverConfig, this.deviceModel);
    const testSchedule = this.createTestSchedule(testPointA, collectorURL);
    const controllerConfig = this.createControllerConfig(testPointA, testSchedule);
    if (!this.output.scheduler?.repeatInterval) {
      this.runType = RunTypeValue.ON_DEMAND;
    }
    return JSON.stringify(controllerConfig);
  }

  //eslint-disable-next-line
  createTestSchedule(deviceData: IDeviceData, collectorURL: string): any {
    // create the test schedule
    const testScheduleName = this.output.alias;
    const testActionName = testScheduleName + '-action';
    let testEventName = testScheduleName + '-schedule-event';
    if (this.output.interval < ScheduledIntervals.THIRTY_MINUTES) {
      testEventName = 'immediate';
    }

    const optionArray = this.getTestActionOptions(deviceData);
    const tags = this.getTagInfo(deviceData);
    const action = this.lmapService.createAction(
      testActionName,
      this.testTaskName,
      optionArray,
      null,
      tags
    );
    const actionArray = [];
    actionArray.push(action);
    // while report schedule references could be created here they will be added
    //automatically to this schedule via addResultScheduleAndEvent()
    const testSchedule = this.lmapService.createSchedule(
      testScheduleName,
      testEventName,
      actionArray
    );

    // build the LMAP configuration (test schedule + result schedules + all events other than those predefined by MA such as immediate)
    let lmapified = this.lmapService.addSchedule(testSchedule);

    lmapified = this.lmapService.addResultScheduleAndEvent(
      lmapified,
      testScheduleName,
      testEventName,
      'immediate',
      'pm',
      collectorURL,
      this.output.interval,
      null
    );
    return lmapified;
  }

  //eslint-disable-next-line
  createControllerConfig(deviceData: IDeviceData, lmapified): any {
    const controllerConfig = this.testAssembleService.buildPrimaryTestConfig(
      deviceData,
      this.testTaskName,
      lmapified
    );
    controllerConfig.test['test-list-data']['tpInfo'].push({
      id: this.output.selectedApp.key,
      name: this.output.selectedApp.value,
      type: 'secondary',
      model: 'application',
      url: this.output.selectedApp.subContent
    });
    controllerConfig.test['test-list-data']['runType'] = this.runType;
    controllerConfig.test['test-list-data']['proxyConfig'] = this.output.proxyConfig;
    controllerConfig.test['test-list-data']['interval'] = this.output.interval;
    controllerConfig.test['test-list-data']['scriptUrl'] = this.output.scriptUrl;
    controllerConfig.test['test-list-data']['scriptName'] = this.output.scriptName;
    return controllerConfig;
  }

  //eslint-disable-next-line
  getTestActionOptions(deviceData: IDeviceData): Array<{ id: string; value: string }> {
    const optionArray = [];

    const allTestParams = {};
    allTestParams['multistep'] = this.getAllTestParams();
    const actionOption = {
      id: this.testTaskName,
      value: JSON.stringify(allTestParams)
    };
    optionArray.push({
      id: 'source-ip',
      value: deviceData?.capabilities?.phy['default-test-ip']
        ? deviceData?.capabilities?.phy['default-test-ip']
        : null
    });
    optionArray.push({
      id: 'subnet',
      value: deviceData?.capabilities?.phy['default-test-mask']
        ? deviceData?.capabilities?.phy['default-test-mask']
        : null
    });
    optionArray.push({
      id: 'gateway',
      value: deviceData?.capabilities?.phy['default-test-gateway']
        ? deviceData?.capabilities?.phy['default-test-gateway']
        : null
    });
    optionArray.push(actionOption);
    optionArray.push({ id: 'role', value: 'primary' });
    optionArray.push({ id: 'web-proxy', value: this.output.proxyConfig?.[0]?.value });
    this.output.proxyConfig;
    return optionArray;
  }

  getTagInfo(deviceData: IDeviceData): Array<string> {
    const tags = [];
    tags.push('service-name:' + this.output.selectedApp.value);
    tags.push('monitor-type:' + this.runType);
    tags.push('tenant-id:' + this.tenantId);
    tags.push('application-id:' + this.output.selectedApp.key);
    if (this.output.precheck) {
      tags.push('precheck:' + this.output.precheck);
    }
    if (this.output.scriptName) {
      tags.push('scriptname:' + this.output.scriptName);
    }
    return tags;
  }

  //eslint-disable-next-line
  splitURL(url: string): any {
    return {
      protocol: new URL(url).protocol,
      address: new URL(url).hostname,
      port: new URL(url).port
    };
  }

  //eslint-disable-next-line
  getAllTestParams(): any {
    const splitURL = this.splitURL(this.output.selectedApp.subContent);
    const obj = {
      config: {
        dryrun: this.output.precheck ? true : false,
        'target-ip': {
          'ip-version': 'ipv4',
          'target-address': splitURL.address
        },
        ping: {
          'ping-enable': false,
          'ping-count': 0,
          interval: this.output.interval
        },
        traceroute: {
          'traceroute-enable': this.output.precheck ? false : true,
          'number-of-intervals': this.getNumberOfIntervals(),
          interval: this.output.interval,
          'use-icmpecho': false,
          'use-tcpsyn': true,
          'use-udp': false,
          'tcp-port': splitURL.port || (splitURL.protocol === 'http:' ? 80 : 443)
        },
        pagewalk: {
          'pagewalk-enable': true,
          'number-of-intervals': this.getNumberOfIntervals(),
          interval: this.output.interval
        }
      }
    };
    if (this.output.pageUrl) {
      obj['config']['pagewalk']['page-url'] = this.output.pageUrl;
    }
    if (this.output.scriptUrl) {
      obj['config']['pagewalk']['script-url'] = this.output.scriptUrl;
    }
    return obj;
  }

  getNumberOfIntervals(): number {
    if (this.output.runOnDemand === RunTypeValue.ON_DEMAND) {
      return this.output.precheck ? 1 : (8 * 60) / (this.output.interval / 60);
    }
    return 0;
  }

  /* eslint-disable-next-line */
  _saveControllerTest(testConfig: any): Observable<any> {
    const url = `${this.startTestURL}`;
    /* eslint-disable-next-line */
    return this.apiService.post$<any>(url, testConfig);
  }

}
